import React, { useState, useEffect, useCallback, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { MoonLoader } from "react-spinners";
import { Container, Row, Col } from "react-bootstrap";
import {
  IconCalendar,
  IconUsers,
  IconMessage,
  IconTemplate,
  IconRobot,
  IconTrendingUp,
  IconTrendingDown,
  IconRefresh,
  IconAlertTriangle,
  IconTarget,
  IconBrain,
  IconActivity,
  IconChartPie,
  IconChartBar,
  IconBrandWhatsapp,
  IconUserCheck,
  IconClipboardList,
  IconCalendarStats,
} from "@tabler/icons-react";
import DashboardService from "./DashboardServices";
import "./Dashboard.css";

// =============================================== //
// COMPONENTES DE SKELETON MELHORADOS             //
// =============================================== //
const MetricCardSkeleton = () => (
  <div className="metric-card skeleton slide-up">
    <div className="metric-header">
      <div className="metric-icon skeleton-shimmer"></div>
      <div className="metric-trend skeleton-shimmer skeleton-trend"></div>
    </div>
    <div className="metric-value skeleton-shimmer skeleton-value"></div>
    <div className="metric-label skeleton-shimmer skeleton-label"></div>
    <div className="metric-description skeleton-shimmer skeleton-description"></div>
  </div>
);

const ChartCardSkeleton = ({ height = "300px" }) => (
  <div className="chart-card skeleton slide-up">
    <div className="chart-header">
      <div className="chart-title skeleton-shimmer skeleton-chart-title"></div>
      <div className="chart-period skeleton-shimmer skeleton-chart-period"></div>
    </div>
    <div className="chart-content skeleton-shimmer" style={{ height }}></div>
  </div>
);

// =============================================== //
// COMPONENTE PRINCIPAL DASHBOARD                  //
// =============================================== //
const Dashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Detector de mobile
  const isMobile = useMemo(() => window.innerWidth <= 768, []);

  // =============================================== //
  // FUNÇÕES DE CARREGAMENTO DE DADOS               //
  // =============================================== //
  const loadDashboardData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError(null);

    try {
      const data = await DashboardService.getAllMetrics();
      setMetrics(data);
      
    } catch (err) {
      console.error("Erro ao carregar dashboard:", err);

      // Verificar se é erro de business_segment
      if (err.response?.status === 403) {
        setError(
          "Este dashboard está disponível apenas para segmentos Saúde e Imobiliário."
        );
      } else {
        setError("Erro ao carregar dados do dashboard. Tente novamente.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleRefresh = useCallback(() => {
    DashboardService.clearCache();
    loadDashboardData(true);
    window.location.reload()
  }, [loadDashboardData]);

  // =============================================== //
  // CONFIGURAÇÕES DOS GRÁFICOS OTIMIZADAS          //
  // =============================================== //
  const chartOptions = useMemo(() => {
    const baseOptions = {
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: {
          enabled: !window.matchMedia("(prefers-reduced-motion: reduce)")
            .matches,
          easing: "easeinout",
          speed: 600,
        },
        fontFamily: "inherit",
      },
      grid: {
        borderColor: "#f1f3f4",
        strokeDashArray: 2,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { left: 10, right: 10 },
      },
      xaxis: {
        labels: {
          style: {
            colors: "#6c757d",
            fontSize: isMobile ? "10px" : "11px",
            fontWeight: 500,
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#6c757d",
            fontSize: isMobile ? "10px" : "11px",
            fontWeight: 500,
          },
        },
      },
      tooltip: {
        theme: "light",
        style: { fontSize: "12px" },
        marker: { show: true },
      },
    };

    return {
      // Configuração para gráfico de linha mensal (agendamentos por status) - ALTERADO
      monthlyLine: {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: "line",
          height: isMobile ? 280 : 350,
        },
        colors: [
          "#2bc155", // scheduled - verde
          "var(--primary)", // finished - azul
          "#dc3545", // canceled - vermelho
          "#305CDE", // rescheduled - azul escuro
          "#ff69b4", // missing - rosa
          "#ffc107", // rescheduled_by_ai - amarelo
        ],
        stroke: {
          curve: "smooth",
          width: 3,
        },
        markers: {
          size: 4,
          hover: { size: 6 },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
          fontSize: "11px",
          fontWeight: 500,
          markers: { radius: 3 },
        },
        tooltip: {
          ...baseOptions.tooltip,
          y: {
            formatter: (value) => `${value} agendamentos`,
          },
        },
      },

      // Configuração para gráfico de linha mensal (contatos)
      contactsLine: {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: "line",
          height: isMobile ? 240 : 300,
        },
        colors: ["#305CDE"],
        stroke: {
          curve: "smooth",
          width: 3,
        },
        markers: {
          size: 5,
          hover: { size: 7 },
        },
        tooltip: {
          ...baseOptions.tooltip,
          y: {
            formatter: (value) => `${value} contatos`,
          },
        },
      },

      // Configuração para mensagens
      messages: {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: "area",
          height: isMobile ? 240 : 300,
        },
        colors: ["#ff8c00", "var(--primary)"], // laranja para recebidas, azul para enviadas
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.3,
            opacityTo: 0.05,
            stops: [0, 100],
          },
        },
        stroke: {
          curve: "smooth",
          width: 2,
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
          fontSize: "11px",
          fontWeight: 500,
          markers: { radius: 3 },
        },
      },

      // Configuração para donut (confirmações e contatos)
      donut: {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: "donut",
          height: isMobile ? 220 : 280,
        },
        colors: ["#2bc155", "var(--primary)", "red", "#1ea7c5"],
        legend: {
          position: "bottom",
          fontSize: isMobile ? "10px" : "11px",
          fontWeight: 500,
          markers: { radius: 3 },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "60%",
              labels: {
                show: true,
                total: {
                  show: true,
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: 600,
                  color: "#495057",
                },
              },
            },
          },
        },
        tooltip: {
          ...baseOptions.tooltip,
          y: {
            formatter: (value) => `${value} itens`,
          },
        },
      },

      // Configuração para IA vs Manual diário
      aiVsManual: {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: "line",
          height: isMobile ? 240 : 300, // Ajustado para ficar igual ao mensagens
        },
        colors: ["#2bc155", "var(--primary)"],
        stroke: {
          curve: "smooth",
          width: 3,
        },
        markers: {
          size: 5,
          hover: { size: 7 },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
          fontSize: "12px",
          fontWeight: 600,
          markers: { width: 8, height: 8, radius: 2 },
        },
      },
    };
  }, [isMobile]);

  // =============================================== //
  // PREPARAÇÃO DOS DADOS DOS GRÁFICOS REFATORADA   //
  // =============================================== //
  const chartData = useMemo(() => {
    if (!metrics) return {};

    // 1. AGENDAMENTOS POR STATUS MENSAL (6 meses) - ALTERADO PARA LINE CHART
    const appointmentsByStatusData =
      metrics.charts?.appointments_by_status_monthly || {};
    const appointmentsSeries = [];
    const appointmentsCategories = [];

    if (Object.keys(appointmentsByStatusData).length > 0) {
      const statusTypes = [
        "scheduled",
        "finished",
        "canceled",
        "rescheduled",
        "missing",
        "rescheduled_by_ai",
      ];
      const statusLabels = {
        scheduled: "Agendados",
        finished: "Concluídos",
        canceled: "Cancelados",
        rescheduled: "Reagendados",
        missing: "Faltaram",
        rescheduled_by_ai: "Reagend. IA",
      };

      // Extrair categorias (meses)
      appointmentsCategories.push(
        ...Object.keys(appointmentsByStatusData).sort()
      );

      // Criar séries para cada status
      statusTypes.forEach((status) => {
        if (statusLabels[status]) {
          const data = appointmentsCategories.map(
            (month) => appointmentsByStatusData[month]?.[status] || 0
          );
          appointmentsSeries.push({
            name: statusLabels[status],
            data: data,
          });
        }
      });
    }

    // 2. DISTRIBUIÇÃO DE CONFIRMAÇÕES
    const confirmationData = metrics.charts?.confirmation_distribution || {};
    const confirmationSeries = Object.values(confirmationData);
    const confirmationLabels = Object.keys(confirmationData).map((key) => {
      const labels = {
        confirmed: "Confirmados",
        pending: "Pendentes",
        canceled: "Cancelados",
        not_confirmed: "Não confirmaram",
      };
      return labels[key] || key;
    });

    // 3. CONTATOS POR MÊS (6 meses)
    const contactsTrendData = metrics.charts?.contacts_monthly_trend || {};
    const contactsCategories = Object.keys(contactsTrendData).sort();
    const contactsData = contactsCategories.map(
      (month) => contactsTrendData[month] || 0
    );

    // 4. MENSAGENS POR DIA - ALTERADO: apenas 15 dias (7 no mobile)
    const messagesData = metrics.charts?.messages_by_date || [];
    const messageDaysLimit = isMobile ? 7 : 15;
    const limitedMessagesData = messagesData.slice(-messageDaysLimit);

    const messagesCategories = limitedMessagesData.map(
      (item) => item.formatted_date
    );
    const receivedData = limitedMessagesData.map((item) => item.received);
    const sentData = limitedMessagesData.map((item) => item.sent);

    // 5. AI VS MANUAL DIÁRIO - ALTERADO: apenas 15 dias (7 no mobile)
    const aiVsManualData = metrics.charts?.ai_vs_manual_daily || {};
    const aiDaysLimit = isMobile ? 7 : 15;

    const allAiCategories = aiVsManualData.categories || [];
    const allAiSeriesData = aiVsManualData.ai_series || [];
    const allManualSeriesData = aiVsManualData.manual_series || [];

    const aiCategories = allAiCategories.slice(-aiDaysLimit);
    const aiSeriesData = allAiSeriesData.slice(-aiDaysLimit);
    const manualSeriesData = allManualSeriesData.slice(-aiDaysLimit);

    // 6. CONTATOS POR COLUNA (mantido)
    const contactsByColumn =
      metrics.operational?.contacts?.contacts_by_column || {};
    const contactsByColumnSeries = Object.values(contactsByColumn);
    const contactsByColumnLabels = Object.keys(contactsByColumn);

    return {
      appointmentsByStatus: {
        series: appointmentsSeries,
        categories: appointmentsCategories,
      },
      confirmationDistribution: {
        series: confirmationSeries,
        labels: confirmationLabels,
      },
      contactsMonthlyTrend: {
        series: [
          {
            name: "Novos Contatos",
            data: contactsData,
          },
        ],
        categories: contactsCategories,
      },
      messages: {
        series: [
          { name: "Recebidas", data: receivedData },
          { name: "Enviadas", data: sentData },
        ],
        categories: messagesCategories,
      },
      aiVsManual: {
        series: [
          { name: "Agendamentos IA", data: aiSeriesData },
          { name: "Agendamentos Manuais", data: manualSeriesData },
        ],
        categories: aiCategories,
      },
      contactsByColumn: {
        series: contactsByColumnSeries,
        labels: contactsByColumnLabels,
      },
    };
  }, [metrics, isMobile]);

  // =============================================== //
  // MÉTRICAS PRINCIPAIS REFATORADAS (4 CARDS)      //
  // =============================================== //
  const primaryMetrics = useMemo(() => {
    if (!metrics?.primary_metrics) return [];

    return [
      {
        id: "manual_appointments",
        icon: IconUserCheck,
        value: metrics.primary_metrics.manual_appointments?.current_month || 0,
        label: "Agendamentos Manuais",
        description: `${
          metrics.primary_metrics.manual_appointments?.previous_month || 0
        } no mês anterior`,
        trend: metrics.primary_metrics.manual_appointments?.trend || "neutral",
        growth: `${
          metrics.primary_metrics.manual_appointments?.growth_percentage || 0
        }%`,
        priority: 1,
        color: "linear-gradient(135deg, #305CDE, #1ea7c5)",
      },
      {
        id: "ai_appointments",
        icon: IconRobot,
        value: metrics.primary_metrics.ai_appointments?.current_month || 0,
        label: "Agendamentos IA",
        description: `${
          metrics.primary_metrics.ai_appointments?.previous_month || 0
        } no mês anterior`,
        trend: metrics.primary_metrics.ai_appointments?.trend || "neutral",
        growth: `${
          metrics.primary_metrics.ai_appointments?.growth_percentage || 0
        }%`,
        priority: 2,
        color: "linear-gradient(135deg, #2bc155, #20c997)",
      },
      {
        id: "conversion_rate",
        icon: IconTarget,
        value: `${
          metrics.primary_metrics.conversion_rate?.conversion_rate || 0
        }%`,
        label: "Taxa de Conversão", 
        description: `${
          metrics.primary_metrics.conversion_rate?.contacts_with_customers || 0  
        } de ${
          metrics.primary_metrics.conversion_rate?.total_contacts || 0
        } contatos`, 
        trend: metrics.primary_metrics.conversion_rate?.trend || "neutral",
        growth: "Contatos → Clientes", 
        priority: 3,
        color: "linear-gradient(135deg, var(--primary), #de3096)",
      },
      {
        id: "contacts_monthly",
        icon: IconUsers,
        value: metrics.primary_metrics.contacts_monthly?.current_month || 0,
        label: "Contatos Mensais",
        description: `${
          metrics.primary_metrics.contacts_monthly?.previous_month || 0
        } no mês anterior`,
        trend: metrics.primary_metrics.contacts_monthly?.trend || "neutral",
        growth: `${
          metrics.primary_metrics.contacts_monthly?.growth_percentage || 0
        }%`,
        priority: 4,
        color: "linear-gradient(135deg, #1ea7c5, #305CDE)",
      },
    ];
  }, [metrics]);

  // =============================================== //
  // MÉTRICAS OPERACIONAIS (2ª LINHA) - ALTERADO   //
  // =============================================== //
  const operationalMetrics = useMemo(() => {
    if (!metrics) return [];

    const totalMessages = (metrics.charts?.messages_by_date || []).reduce(
      (acc, item) => acc + item.received + item.sent,
      0
    );

    const totalContacts = metrics.operational?.contacts?.total_contacts || 0;

    // NOVA MÉTRICA: Total de Agendamentos
    const totalAppointments = metrics.primary_metrics?.total_appointments || {};
    return [
      {
        id: "total_appointments",
        icon: IconCalendarStats,
        value: totalAppointments.total_all_time || 0,
        label: "Total de Agendamentos",
        description: `${totalAppointments.ai_percentage || 0}% IA | ${
          totalAppointments.manual_percentage || 0
        }% Manual`,
        trend: totalAppointments.total_all_time > 0 ? "positive" : "neutral",
        growth: "Histórico completo",
        color: "linear-gradient(135deg, #6f42c1, #e83e8c)",
      },
      {
        id: "total_contacts",
        icon: IconUsers,
        value: totalContacts,
        label: "Total de Contatos",
        description: `${
          Object.keys(metrics.operational?.contacts?.contacts_by_column || {})
            .length
        } colunas ativas`,
        trend: totalContacts > 0 ? "positive" : "neutral",
        growth: "Total geral",
        color: "linear-gradient(135deg, #2bc155, #20c997)",
      },
      {
        id: "total_messages",
        icon: IconMessage,
        value: totalMessages,
        label: "Total de Mensagens",
        description: `${(metrics.charts?.messages_by_date || []).reduce(
          (acc, item) => acc + item.received,
          0
        )} recebidas | ${(metrics.charts?.messages_by_date || []).reduce(
          (acc, item) => acc + item.sent,
          0
        )} enviadas`,
        trend: totalMessages > 0 ? "positive" : "neutral",
        growth: "Últimos 30 dias",
        color: "linear-gradient(135deg, #1ea7c5, #305CDE)",
      },
    ];
  }, [metrics]);

  // =============================================== //
  // RENDERIZAÇÃO DOS SKELETONS                      //
  // =============================================== //
  if (loading && !refreshing) {
    return (
      <div className="main-container">
        <div className="dashboard-grid grid-4">
          {[1, 2, 3, 4].map((i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>

        <div
          className="dashboard-grid grid-3"
          style={{ marginTop: "var(--spacing-xl)" }}
        >
          {[1, 2, 3].map((i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>

        <Row className="mt-4">
          <Col xs={12} lg={8} className="mb-3">
            <ChartCardSkeleton />
          </Col>
          <Col xs={12} lg={4} className="mb-3">
            <ChartCardSkeleton height="280px" />
          </Col>
        </Row>

        <Row className="mt-4">
          <Col xs={12} lg={8} className="mb-3">
            <ChartCardSkeleton />
          </Col>
          <Col xs={12} lg={4} className="mb-3">
            <ChartCardSkeleton height="280px" />
          </Col>
        </Row>
      </div>
    );
  }

  // =============================================== //
  // RENDERIZAÇÃO DE ERRO                            //
  // =============================================== //
  if (error) {
    return (
      <div className="main-container">
        <div className="error-container">
          <IconAlertTriangle className="error-icon" />
          <h3 className="error-title">Ops! Algo deu errado</h3>
          <p className="error-message">{error}</p>
          <button className="error-button" onClick={handleRefresh}>
            <IconRefresh size={16} />
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  // =============================================== //
  // RENDERIZAÇÃO PRINCIPAL                          //
  // =============================================== //
  return (
    <div className="main-container dashboard" style={{ paddingTop: 100 }}>
      {/* 1ª LINHA - MÉTRICAS PRINCIPAIS (4 CARDS) */}
      <section className="metrics-section">
        <div className="section-header">
          <h2 className="section-title">
            <IconChartBar size={20} />
            Métricas Principais
          </h2>
          {metrics?.metadata && (
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? (
                <MoonLoader size={12} />
              ) : (
                <IconRefresh className="mr-1" size={16} />
              )}
              {window.innerWidth > 768 ? refreshing ? "Atualizando..." : "Atualizar" : null}
            </button>
          )}
        </div>

        <div className="dashboard-grid grid-4">
          {primaryMetrics.map((metric, index) => {
            const IconComponent = metric.icon;
            return (
              <div
                key={metric.id}
                className="metric-card fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="metric-header">
                  <div
                    className="metric-icon"
                    style={metric.color ? { background: metric.color } : {}}
                  >
                    <IconComponent size={20} />
                  </div>
                  <div className={`metric-trend ${metric.trend}`}>
                    {metric.trend === "positive" ? (
                      <IconTrendingUp size={10} />
                    ) : metric.trend === "negative" ? (
                      <IconTrendingDown size={10} />
                    ) : (
                      <span style={{ fontSize: "8px" }}></span>
                    )}
                    {metric.growth}
                  </div>
                </div>
                <div className="metric-value">{metric.value}</div>
                <h3 className="metric-label">{metric.label}</h3>
                <p className="metric-description">{metric.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* 2ª LINHA - MÉTRICAS OPERACIONAIS (3 CARDS) - ALTERADO */}
      <section className="metrics-section">
        <div className="section-header">
          <h2 className="section-title">
            <IconActivity size={20} />
            Métricas Operacionais
          </h2>
        </div>

        <div className="dashboard-grid grid-3">
          {operationalMetrics.map((metric, index) => {
            const IconComponent = metric.icon;
            return (
              <div
                key={metric.id}
                className="metric-card secondary fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="metric-header">
                  <div
                    className="metric-icon"
                    style={metric.color ? { background: metric.color } : {}}
                  >
                    <IconComponent size={20} />
                  </div>
                  <div className={`metric-trend ${metric.trend}`}>
                    {metric.trend === "positive" ? (
                      <IconTrendingUp size={10} />
                    ) : (
                      <span style={{ fontSize: "8px" }}>—</span>
                    )}
                    {metric.growth}
                  </div>
                </div>
                <div className="metric-value">{metric.value}</div>
                <h3 className="metric-label">{metric.label}</h3>
                <p className="metric-description">{metric.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* 3ª LINHA - ANÁLISE MENSAL */}
      <section className="charts-section">
        <div className="section-header">
          <h2 className="section-title">
            <IconCalendar size={20} />
            Análise Mensal
          </h2>
        </div>

        <Row>
          <Col xs={12} lg={8} className="mb-4">
            <div className="chart-card fade-in">
              <div className="chart-header">
                <h3 className="chart-title">
                  <IconActivity size={16} />
                  Evolução de Agendamentos por Status
                </h3>
                <span className="chart-period">
                  Últimos 6 meses - Tendências
                </span>
              </div>
              <div className="chart-content">
                {chartData.appointmentsByStatus?.series?.length > 0 ? (
                  <ReactApexChart
                    options={{
                      ...chartOptions.monthlyLine,
                      xaxis: {
                        ...chartOptions.monthlyLine.xaxis,
                        categories: chartData.appointmentsByStatus.categories,
                      },
                    }}
                    series={chartData.appointmentsByStatus.series}
                    type="line"
                    height={350}
                  />
                ) : (
                  <div className="chart-no-data">
                    <IconActivity
                      size={32}
                      style={{ opacity: 0.3, marginBottom: "8px" }}
                    />
                    <p>Nenhum dado de agendamentos disponível</p>
                  </div>
                )}
              </div>
            </div>
          </Col>

          <Col xs={12} lg={4} className="mb-4">
            <div
              className="chart-card fade-in"
              style={{ animationDelay: "0.1s" }}
            >
              <div className="chart-header">
                <h3 className="chart-title">
                  <IconUserCheck size={16} />
                  Status de Confirmação
                </h3>
                <span className="chart-period">Total geral</span>
              </div>
              <div className="chart-content">
                {chartData.confirmationDistribution?.series?.length > 0 ? (
                  <ReactApexChart
                    options={{
                      ...chartOptions.donut,
                      labels: chartData.confirmationDistribution.labels,
                    }}
                    series={chartData.confirmationDistribution.series}
                    type="donut"
                    height={280}
                  />
                ) : (
                  <div className="chart-no-data">
                    <IconChartPie
                      size={32}
                      style={{ opacity: 0.3, marginBottom: "8px" }}
                    />
                    <p>Nenhum dado de confirmação disponível</p>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </section>

      <section className="charts-section">
        <div className="section-header">
          <h2 className="section-title">
            <IconBrain size={20} />
            IA & Comunicação
          </h2>
        </div>

        <Row>
          <Col xs={12} lg={6} className="mb-4">
            <div className="chart-card fade-in">
              <div className="chart-header">
                <h3 className="chart-title">
                  <IconMessage size={16} />
                  Mensagens por Dia
                </h3>
                <span className="chart-period">
                  Últimos {isMobile ? "7" : "15"} dias
                </span>
              </div>
              <div className="chart-content">
                {chartData.messages?.series?.[0]?.data?.length > 0 ? (
                  <ReactApexChart
                    options={{
                      ...chartOptions.messages,
                      xaxis: {
                        ...chartOptions.messages.xaxis,
                        categories: chartData.messages.categories,
                      },
                    }}
                    series={chartData.messages.series}
                    type="area"
                    height={300}
                  />
                ) : (
                  <div className="chart-no-data">
                    <IconMessage
                      size={32}
                      style={{ opacity: 0.3, marginBottom: "8px" }}
                    />
                    <p>Nenhum dado de mensagens disponível</p>
                  </div>
                )}
              </div>
            </div>
          </Col>

          <Col xs={12} lg={6} className="mb-4">
            <div
              className="chart-card fade-in"
              style={{ animationDelay: "0.1s" }}
            >
              <div className="chart-header">
                <h3 className="chart-title">
                  <IconRobot size={16} />
                  IA vs Manual (Diário)
                </h3>
                <span className="chart-period">
                  Últimos {isMobile ? "7" : "15"} dias
                </span>
              </div>
              <div className="chart-content">
                {chartData.aiVsManual?.series?.[0]?.data?.length > 0 ? (
                  <ReactApexChart
                    options={{
                      ...chartOptions.aiVsManual,
                      xaxis: {
                        ...chartOptions.aiVsManual.xaxis,
                        categories: chartData.aiVsManual.categories,
                      },
                    }}
                    series={chartData.aiVsManual.series}
                    type="line"
                    height={300}
                  />
                ) : (
                  <div className="chart-no-data">
                    <IconRobot
                      size={32}
                      style={{ opacity: 0.3, marginBottom: "8px" }}
                    />
                    <p>Nenhum dado de IA disponível</p>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* 5ª LINHA - CONTATOS E DISTRIBUIÇÃO */}
      <section className="charts-section">
        <div className="section-header">
          <h2 className="section-title">
            <IconUsers size={20} />
            Gestão de Contatos
          </h2>
        </div>

        <Row>
          <Col xs={12} lg={8} className="mb-4">
            <div className="chart-card fade-in">
              <div className="chart-header">
                <h3 className="chart-title">
                  <IconTrendingUp size={16} />
                  Novos Contatos por Mês
                </h3>
                <span className="chart-period">Últimos 6 meses</span>
              </div>
              <div className="chart-content">
                {chartData.contactsMonthlyTrend?.series?.[0]?.data?.length >
                0 ? (
                  <ReactApexChart
                    options={{
                      ...chartOptions.contactsLine,
                      xaxis: {
                        ...chartOptions.contactsLine.xaxis,
                        categories: chartData.contactsMonthlyTrend.categories,
                      },
                    }}
                    series={chartData.contactsMonthlyTrend.series}
                    type="line"
                    height={300}
                  />
                ) : (
                  <div className="chart-no-data">
                    <IconUsers
                      size={32}
                      style={{ opacity: 0.3, marginBottom: "8px" }}
                    />
                    <p>Nenhum dado de contatos disponível</p>
                  </div>
                )}
              </div>
            </div>
          </Col>

          <Col xs={12} lg={4} className="mb-4">
            <div
              className="chart-card fade-in"
              style={{ animationDelay: "0.1s" }}
            >
              <div className="chart-header">
                <h3 className="chart-title">
                  <IconChartPie size={16} />
                  Distribuição por Coluna
                </h3>
                <span className="chart-period">Total por funil</span>
              </div>
              <div className="chart-content">
                {chartData.contactsByColumn?.series?.length > 0 ? (
                  <ReactApexChart
                    options={{
                      ...chartOptions.donut,
                      labels: chartData.contactsByColumn.labels,
                    }}
                    series={chartData.contactsByColumn.series}
                    type="donut"
                    height={280}
                  />
                ) : (
                  <div className="chart-no-data">
                    <IconChartPie
                      size={32}
                      style={{ opacity: 0.3, marginBottom: "8px" }}
                    />
                    <p>Nenhum dado de distribuição disponível</p>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default Dashboard;
